import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle2, Loader2, Lock, Building2, Layers3 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export type ImportMode = "single" | "multi";

export type ImportScope = {
  channelId?: number;
  companyId?: number;
  channelName?: string;
  companyName?: string;
  isValid?: boolean;
  rootCompanyId?: number;
  rootCompanyName?: string;
  availableCompanyCount?: number;
  allowsSingleImport?: boolean;
  allowsMultiImport?: boolean;
  isHierarchicalScope?: boolean;
};

interface ImportScopeSelectorProps {
  onScopeChange: (scope: ImportScope | null) => void;
  disabled?: boolean;
  mode?: ImportMode;
}

function getEntityDisplayName(entity: any, fallback = "Não informado") {
  return entity?.nomeFantasia || entity?.name || entity?.razaoSocial || fallback;
}

export function ImportScopeSelector({
  onScopeChange,
  disabled = false,
  mode = "single",
}: ImportScopeSelectorProps) {
  const { user } = useAuth();
  const [selectedScope, setSelectedScope] = useState<ImportScope | null>(null);
  const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);

  const { data: scopesData, isLoading: scopesLoading, error: scopesError } = trpc.imports.getAvailableScopes.useQuery(undefined, {
    retry: 2,
    retryDelay: 1000,
  });

  const { data: companiesData, isLoading: companiesLoading } = trpc.imports.getCompaniesByChannel.useQuery(
    { channelId: selectedChannelId! },
    { enabled: !!selectedChannelId && ["admin", "salvia_operation_admin"].includes(typeof user?.role === "string" ? user.role : ""), retry: 2 },
  );

  const data = (scopesData?.data as any) || {};
  const userRole = typeof user?.role === "string" ? user.role : "";
  const isAdminSalvia = ["admin", "salvia_operation_admin"].includes(userRole);
  const isAdminParceiro = userRole === "channel_admin" || userRole === "channel_admin_multi";
  const isOperadorCanal = userRole === "channel_operator";
  const isParceiroOuOperador = isAdminParceiro || isOperadorCanal;
  const isRhCliente = userRole === "client_rh";
  const isGlobalAdminMultiMode = isAdminSalvia && mode === "multi";

  const rhRootCompany = data.rootCompany || data.company || null;
  const rhChannel = data.channel || null;
  const rhScopeCompanies = useMemo(() => {
    if (Array.isArray(data.companies) && data.companies.length > 0) {
      return data.companies;
    }

    return rhRootCompany ? [rhRootCompany] : [];
  }, [data.companies, rhRootCompany]);
  const rhAllowsMultiImport = !!data.allowsMultiImport;
  const rhAllowsSingleImport = data.allowsSingleImport !== false;
  const rhHasSelectableCompanies = isRhCliente && rhAllowsMultiImport && !data.noSelection && rhScopeCompanies.length > 0;
  const rhRootCompanyName = getEntityDisplayName(rhRootCompany);
  const rhChannelName = getEntityDisplayName(rhChannel, "Canal não identificado");

  useEffect(() => {
    if (isGlobalAdminMultiMode) {
      setSelectedChannelId(null);
      setSelectedCompanyId(null);
      if (selectedScope !== null) {
        setSelectedScope(null);
        onScopeChange(null);
      }
      return;
    }

    if (!scopesData?.data) return;

    if (isAdminSalvia && Array.isArray(data.channels) && data.channels.length === 1) {
      setSelectedChannelId((current) => current ?? data.channels[0].id);
    }

    if (isParceiroOuOperador && Array.isArray(data.companies) && data.companies.length === 1) {
      const onlyCompany = data.companies[0];
      setSelectedCompanyId(onlyCompany.id);
      const newScope = {
        companyId: onlyCompany.id,
        companyName: getEntityDisplayName(onlyCompany),
        channelId: data.channel?.id,
        channelName: getEntityDisplayName(data.channel),
        isValid: true,
        allowsSingleImport: true,
        allowsMultiImport: false,
      } satisfies ImportScope;
      setSelectedScope(newScope);
      onScopeChange(newScope);
    }

    if (isRhCliente && rhRootCompany?.id) {
      if (mode === "multi" || !rhHasSelectableCompanies) {
        const rhScope: ImportScope = {
          companyId: rhRootCompany.id,
          companyName: rhRootCompanyName,
          channelId: rhChannel?.id,
          channelName: rhChannelName,
          isValid: true,
          rootCompanyId: rhRootCompany.id,
          rootCompanyName: rhRootCompanyName,
          availableCompanyCount: rhScopeCompanies.length,
          allowsSingleImport: rhAllowsSingleImport,
          allowsMultiImport: rhAllowsMultiImport,
          isHierarchicalScope: rhHasSelectableCompanies,
        };
        setSelectedCompanyId(rhRootCompany.id);
        setSelectedScope(rhScope);
        onScopeChange(rhScope);
        return;
      }

      const currentSelectedStillValid = rhScopeCompanies.some((company: any) => company.id === selectedCompanyId);
      const defaultCompany = currentSelectedStillValid
        ? rhScopeCompanies.find((company: any) => company.id === selectedCompanyId)
        : rhScopeCompanies.find((company: any) => company.id === rhRootCompany.id) || rhScopeCompanies[0];

      if (defaultCompany?.id && defaultCompany.id !== selectedCompanyId) {
        setSelectedCompanyId(defaultCompany.id);
      }
    }

    if (data.error) {
      console.error("Erro ao obter escopos:", data.error);
    }
  }, [
    data,
    isAdminSalvia,
    isParceiroOuOperador,
    isRhCliente,
    mode,
    onScopeChange,
    rhAllowsMultiImport,
    rhAllowsSingleImport,
    rhChannel?.id,
    rhChannelName,
    rhHasSelectableCompanies,
    rhRootCompany?.id,
    rhRootCompanyName,
    rhScopeCompanies,
    scopesData,
    selectedCompanyId,
  ]);

  useEffect(() => {
    if (isGlobalAdminMultiMode) {
      if (selectedScope !== null) {
        setSelectedScope(null);
        onScopeChange(null);
      }
      return;
    }

    if (isRhCliente) {
      if (mode === "multi" || !rhHasSelectableCompanies) {
        return;
      }

      if (!selectedCompanyId) {
        setSelectedScope(null);
        onScopeChange(null);
        return;
      }

      const company = rhScopeCompanies.find((item: any) => item.id === selectedCompanyId);
      const newScope: ImportScope = {
        companyId: selectedCompanyId,
        companyName: getEntityDisplayName(company),
        channelId: rhChannel?.id,
        channelName: rhChannelName,
        isValid: !!selectedCompanyId,
        rootCompanyId: rhRootCompany?.id,
        rootCompanyName: rhRootCompanyName,
        availableCompanyCount: rhScopeCompanies.length,
        allowsSingleImport: rhAllowsSingleImport,
        allowsMultiImport: rhAllowsMultiImport,
        isHierarchicalScope: true,
      };

      setSelectedScope(newScope);
      onScopeChange(newScope);
      return;
    }

    if (!selectedCompanyId) {
      setSelectedScope(null);
      onScopeChange(null);
      return;
    }

    const companies = companiesData?.data || data?.companies || [];
    const company = companies.find((item: any) => item.id === selectedCompanyId);

    let isValid = false;
    if (isAdminSalvia) {
      isValid = !!selectedChannelId && !!selectedCompanyId;
    } else if (isParceiroOuOperador) {
      isValid = !!selectedCompanyId;
    }

    const channelData = data?.channel;
    const newScope: ImportScope = {
      companyId: selectedCompanyId,
      companyName: getEntityDisplayName(company),
      channelId: selectedChannelId || channelData?.id || undefined,
      channelName: getEntityDisplayName(channelData, "Canal não informado"),
      isValid,
      allowsSingleImport: true,
      allowsMultiImport: isAdminSalvia,
    };

    setSelectedScope(newScope);
    onScopeChange(newScope);
  }, [
    companiesData,
    data,
    isAdminSalvia,
    isParceiroOuOperador,
    isRhCliente,
    mode,
    onScopeChange,
    rhAllowsMultiImport,
    rhAllowsSingleImport,
    rhChannel?.id,
    rhChannelName,
    rhHasSelectableCompanies,
    rhRootCompany?.id,
    rhRootCompanyName,
    rhScopeCompanies,
    selectedChannelId,
    selectedCompanyId,
  ]);

  if (isRhCliente) {
    if (scopesLoading) {
      return (
        <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 p-5">
          <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
          <div>
            <p className="text-sm font-medium text-blue-900">Carregando informações da empresa...</p>
            <p className="mt-0.5 text-xs text-blue-700">Identificando sua empresa vinculada</p>
          </div>
        </div>
      );
    }

    if (scopesError || data?.error) {
      return (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{data?.error || "Erro ao carregar informações da empresa. Tente recarregar a página."}</AlertDescription>
        </Alert>
      );
    }

    if (!rhRootCompany?.id) {
      return (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Seu usuário não está vinculado a uma empresa. Contate o administrador para regularizar seu cadastro.
          </AlertDescription>
        </Alert>
      );
    }

    const eligibleCompanyPreview = rhScopeCompanies
      .slice(0, 5)
      .map((company: any) => getEntityDisplayName(company))
      .join(", ");

    if (mode === "single" && rhHasSelectableCompanies) {
      return (
        <div className="space-y-4">
          <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <label className="text-sm font-medium text-gray-700">Selecione a empresa do envio simples</label>
              <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                {rhScopeCompanies.length} empresas disponíveis
              </Badge>
            </div>

            <ComboboxSelect
              options={rhScopeCompanies.map((company: any) => ({
                value: company.id.toString(),
                label: getEntityDisplayName(company),
              }))}
              value={selectedCompanyId?.toString() || ""}
              onValueChange={(value) => setSelectedCompanyId(Number(value))}
              placeholder="Escolha a empresa do envio simples..."
              searchPlaceholder="Busque pelo nome da empresa..."
              emptyText="Nenhuma empresa encontrada no escopo."
              disabled={disabled}
            />

            <p className="text-xs leading-5 text-slate-500">
              Escolha a empresa do seu escopo para receber esta importação simples.
            </p>
          </div>
        </div>
      );
    }

    if (rhHasSelectableCompanies && mode === "multi") {
      return (
        <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-gray-700">Empresas disponíveis para esta importação multiempresa</p>
              <Badge variant="secondary" className="bg-violet-100 text-violet-800">
                {rhScopeCompanies.length} elegíveis
              </Badge>
            </div>
            <p className="text-xs leading-5 text-slate-500">
              O arquivo multiempresa pode distribuir linhas apenas entre as empresas abaixo.
            </p>
          </div>

          <div className="max-h-64 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3">
            {rhScopeCompanies.map((company: any) => {
              const companyName = getEntityDisplayName(company);
              const isRootCompany = company.id === rhRootCompany?.id;
              return (
                <div
                  key={company.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">{companyName}</p>
                    <p className="text-xs text-slate-500">{company.cnpj || rhChannelName}</p>
                  </div>
                  {isRootCompany ? (
                    <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                      Empresa raiz
                    </Badge>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-5">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
              <Building2 className="h-5 w-5 text-emerald-700" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-2">
                <p className="text-sm font-semibold text-emerald-900">Empresa vinculada ao seu perfil</p>
                <Lock className="h-3.5 w-3.5 text-emerald-600" />
              </div>
              <p className="truncate text-base font-medium text-emerald-800">{rhRootCompanyName}</p>
              <p className="mt-1 text-xs text-emerald-600">Canal: {rhChannelName}</p>
            </div>
            <Badge variant="secondary" className="flex-shrink-0 bg-emerald-200 text-xs text-emerald-800">
              <Lock className="mr-1 h-3 w-3" />
              Fixo
            </Badge>
          </div>
          <p className="mt-3 text-xs text-emerald-600">
            A importação será realizada para esta empresa. Este campo não pode ser alterado.
          </p>
        </div>
      </div>
    );
  }

  if (isParceiroOuOperador) {
    const companies = (data?.companies || []) as any[];

    if (scopesLoading) {
      return (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
          <span className="text-sm text-blue-700">Carregando clientes disponíveis...</span>
        </div>
      );
    }

    if (scopesError || data?.error) {
      return (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{data?.error || "Erro ao carregar clientes"}</AlertDescription>
        </Alert>
      );
    }

    if (companies.length === 0) {
      return (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Você não possui clientes cadastrados. Contate o administrador para adicionar clientes ao seu canal.
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">Selecione o Cliente</label>
          {selectedCompanyId ? (
            <Badge variant="secondary" className="bg-amber-200 text-amber-900">
              Selecionado
            </Badge>
          ) : null}
        </div>
        <ComboboxSelect
          options={companies.map((company: any) => ({
            value: company.id.toString(),
            label: getEntityDisplayName(company),
          }))}
          value={selectedCompanyId?.toString() || ""}
          onValueChange={(value) => setSelectedCompanyId(Number(value))}
          placeholder="Escolha um cliente..."
          searchPlaceholder="Busque pelo nome do cliente..."
          emptyText="Nenhum cliente encontrado."
          disabled={disabled}
        />
      </div>
    );
  }

  if (isAdminSalvia) {
    if (isGlobalAdminMultiMode) {
      return (
        <div className="space-y-4 rounded-lg border border-violet-200 bg-violet-50 p-4">
          <div className="space-y-2">
            <p className="text-sm font-semibold text-violet-950">Modo multiempresa global liberado</p>
            <p className="text-sm leading-6 text-violet-900">
              Para este perfil, não é necessário definir canal ou cliente antes do envio. O destino de cada linha será resolvido pela coluna de CNPJ da planilha multiempresa.
            </p>
          </div>
          <div className="rounded-md border border-violet-200 bg-white p-3 text-sm text-violet-950">
            Revise o layout multiempresa e envie o arquivo. Se algum CNPJ não estiver cadastrado ou não for elegível, a validação retornará a linha com erro amigável.
          </div>
        </div>
      );
    }

    const channels = (data?.channels || []) as any[];
    const companies = (companiesData?.data || []) as any[];

    if (scopesLoading) {
      return (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
          <span className="text-sm text-blue-700">Carregando canais e clientes...</span>
        </div>
      );
    }

    if (scopesError) {
      return (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Erro ao carregar canais e clientes</AlertDescription>
        </Alert>
      );
    }

    if (channels.length === 0) {
      return (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Nenhum canal disponível. Contate o administrador.</AlertDescription>
        </Alert>
      );
    }

    return (
      <div className="space-y-4 rounded-lg border border-purple-200 bg-purple-50 p-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Selecione o Canal *</label>
          <ComboboxSelect
            options={channels.map((channel: any) => ({
              value: channel.id.toString(),
              label: getEntityDisplayName(channel),
            }))}
            value={selectedChannelId?.toString() || ""}
            onValueChange={(value) => {
              const newChannelId = Number(value);
              if (newChannelId !== selectedChannelId) {
                setSelectedChannelId(newChannelId);
                setSelectedCompanyId(null);
              }
            }}
            placeholder="Escolha um canal..."
            searchPlaceholder="Busque pelo nome do canal..."
            emptyText="Nenhum canal encontrado."
            disabled={disabled}
          />
        </div>

        {selectedChannelId ? (
          <div className="space-y-2">
            {companiesLoading ? (
              <div className="flex items-center gap-2 rounded border border-purple-200 bg-white p-2">
                <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                <span className="text-sm text-purple-700">Carregando clientes...</span>
              </div>
            ) : companies.length === 0 ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>Este canal não possui clientes cadastrados.</AlertDescription>
              </Alert>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Selecione o Cliente *</label>
                  {selectedCompanyId ? (
                    <Badge variant="secondary" className="bg-purple-200 text-purple-900">
                      Selecionado
                    </Badge>
                  ) : null}
                </div>
                <ComboboxSelect
                  options={companies.map((company: any) => ({
                    value: company.id.toString(),
                    label: getEntityDisplayName(company),
                  }))}
                  value={selectedCompanyId?.toString() || ""}
                  onValueChange={(value) => setSelectedCompanyId(Number(value))}
                  placeholder="Escolha um cliente..."
                  searchPlaceholder="Busque pelo nome do cliente..."
                  emptyText="Nenhum cliente encontrado."
                  disabled={disabled}
                />
              </>
            )}
          </div>
        ) : null}

        {selectedScope ? (
          <div className="mt-4 flex items-center gap-2 rounded border border-purple-300 bg-white p-3">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div className="flex-1">
              <p className="text-xs font-medium text-gray-600">Escopo selecionado</p>
              <p className="text-sm text-gray-900">{selectedScope.channelName} → {selectedScope.companyName}</p>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return null;
}
